/**
 * KvList / KvRow — the aside's key/value vocabulary (`record.html` `.kv`;
 * WEB TIER A · WT4).
 *
 * A definition list, because that is what a properties panel IS: `dt` key in
 * `text/secondary` (legal — every KvList sits inside a card on `bg/surface`,
 * the 4.64 pair; the canvas rule in `PageHead` does not apply here), `dd`
 * value right-aligned in `text/primary` at weight 500, a `border/default`
 * hairline between rows.
 *
 * THE HONEST ABSENCE IS THE CALLER'S VALUE, NOT THIS COMPONENT'S GUESS. A row
 * whose fact is not on the wire renders `format.ts`'s NO_VALUE em dash (or a
 * stated word like "Not confirmed") — passed in, so this file cannot invent a
 * default that claims more than the caller knows. Values WRAP rather than
 * truncate: a clipped GSTIN is worse than a two-line one (the mockup's own
 * zero-truncation rule).
 *
 * This is the house replacement for `features/inquiries/panel.tsx`'s
 * `PropertyGroup`/`PropertyRow`, which die with the WT5 slide-over kill
 * (plan §5: "PropertyGroup/PropertyRow/SectionLabel fold into the new KvList").
 */

import type { ReactNode } from 'react';
import { cn } from '../../lib/cn';

export function KvList({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}): React.JSX.Element {
  return <dl className={cn('flex flex-col', className)}>{children}</dl>;
}

export interface KvRowProps {
  label: string;
  /** Mono value — document numbers, references. */
  mono?: boolean;
  children: ReactNode;
}

export function KvRow({ label, mono = false, children }: KvRowProps): React.JSX.Element {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-line py-2.5 last:border-b-0">
      <dt className="w-[132px] shrink-0 text-body13 text-fg-secondary">{label}</dt>
      <dd
        className={cn(
          'min-w-0 flex-1 break-words text-right text-body13 font-medium text-fg',
          mono && 'font-mono text-mono',
        )}
      >
        {children}
      </dd>
    </div>
  );
}
